import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, NgxsOnInit, createSelector } from '@ngxs/store';
import { ViewActions } from './view.actions';
import {
  ViewUIModel,
  UIDefault,
  LogoUI,
  DrawerUI,
  MediaUI,
  LoaderUI,
  LabelsUI,
} from '../models/view-ui.model';
import { Fullscreen } from 'src/app/shared/libraries/fullscreen/fullscreen';
import { ViewUIStatus } from '../models/view-ui-status.enum';
import { InteractTypeEnum } from '../models/interact-type.enum';
import { ViewWindow } from '../models/view-window.enum';
import { TutorialStateModel } from '../models/tutorial-state.model';
import { HelpTypeEnum } from '../models/help-type.enum';
import { WhiteboardStateModel, WhiteboardAnnotateMode } from '../models/whiteboard-state.model';
import {
  DefaultAnnotateBrushSizePreset,
  DefaultAnnotateColorPreset,
  DefaultAnnotateEraserSizePreset,
  DefaultAnnotateTextSizePreset,
} from 'src/app/engine/models/whiteboard-presets.const';
import { ViewInputModeEnum } from '../models/view-input-mode.enum';
import { ViewLayoutConfig } from '../models/view-layout-config.enum';
import { LocalStorageEnum, StorageService } from 'src/app/shared/storage.service';
import { ViewContentSourceEnum } from 'src/app/view/models/view-content-source.enum';
import { ViewScrimModel } from '../models/view-scrim.model';
import { TreeSearchDisplayMode } from 'src/app/shared/models/tree.model';

export class ViewStateModel {
  lang: string;
  canFullscreen: boolean;
  canHover: boolean;
  canTouch: boolean;
  drawerCoversContent: boolean;
  fullscreenActive: boolean;
  help: {
    tab: HelpTypeEnum;
  };
  initialUI: ViewUIModel;
  inputEnabled: boolean;
  inputMode: ViewInputModeEnum;
  interactType: InteractTypeEnum;
  onDemand: boolean;
  labelsBreakpoint: number;
  layout: ViewLayoutConfig;
  menuOpen: boolean;
  multiPickModeEnabled: boolean;
  openWindows: ViewWindow[];
  presentationActive: boolean;
  searchDisplayMode: TreeSearchDisplayMode;
  nodeInfo: {
    show: boolean;
    fallback: ViewContentSourceEnum;
  };
  tutorial: TutorialStateModel;
  tutorialSeen: boolean;
  ui: ViewUIModel;
  usePrintImage: boolean;
  whiteboard: WhiteboardStateModel;
  scrim: ViewScrimModel;
}

export const initialUI: ViewUIModel = {
  audio: new UIDefault(),
  camera: new UIDefault(ViewUIStatus.Show, true), // Camera controls - zoom, pan, reset. Off by default.
  chapterList: new UIDefault(ViewUIStatus.Show, false),
  column: new UIDefault(), // Left description panel, visible on desktop mode
  description: new UIDefault(),
  drawer: new DrawerUI(), // Bottom description panel, visible at 960 below
  fullscreen: new UIDefault(),
  help: new UIDefault(),
  labels: new LabelsUI(),
  labelInfo: new UIDefault(),
  labelList: new UIDefault(ViewUIStatus.Show, false),
  loader: new LoaderUI(),
  logo: new LogoUI(),
  media: new MediaUI(), // Play, pause, scrubber
  menu: new UIDefault(), // Menu button and child items, visible at 960 below
  nodeInfo: new UIDefault(ViewUIStatus.Hide, false),
  pagination: new UIDefault(),
  presenter: new UIDefault(ViewUIStatus.Hide, false),
  reset: new UIDefault(),
  search: new UIDefault(),
  quiz: new UIDefault(),
  titleOnly: new UIDefault(),
  toolbar: new UIDefault(), // Interaction tools, visible in drawer when responsive, floating on the scene in desktop
  tools: new UIDefault(), // tool bar button in menu
  anatomyLabels: new UIDefault(), // anatomy context lavels
  tutorial: new UIDefault(ViewUIStatus.Hide, false), // getting started tour
  internetExplorerWarning: new UIDefault(),
  legend: new UIDefault(),
  undo: new UIDefault(),
  whiteboard: new UIDefault(),
  visibilityLayers: new UIDefault(),
  contextActions: new UIDefault(),
  toggleLabels: new UIDefault(),
};

export const initialViewState: ViewStateModel = {
  lang: 'en',
  canFullscreen: false,
  canHover: false,
  canTouch: false,
  drawerCoversContent: false,
  fullscreenActive: false,
  help: {
    tab: HelpTypeEnum.Basics,
  },
  initialUI: initialUI,
  inputEnabled: true,
  inputMode: ViewInputModeEnum.Mouse,
  interactType: InteractTypeEnum.Play,
  onDemand: false,
  layout: ViewLayoutConfig.Default,
  labelsBreakpoint: 480,
  menuOpen: false,
  multiPickModeEnabled: false,
  openWindows: [ViewWindow.Loader],
  presentationActive: false,
  searchDisplayMode: TreeSearchDisplayMode.Tree,
  nodeInfo: {
    show: false,
    fallback: ViewContentSourceEnum.Wikipedia,
  },
  tutorial: {
    pageSize: 0,
    pageIndex: -1,
  },
  tutorialSeen: true,
  ui: initialUI,
  usePrintImage: false,
  whiteboard: {
    enabled: false,
    hasPaint: false,
    hasText: false,
    brushSize: DefaultAnnotateBrushSizePreset.size,
    brushColor: DefaultAnnotateColorPreset.color,
    textSize: DefaultAnnotateTextSizePreset.size,
    eraserSize: DefaultAnnotateEraserSizePreset.size,
    annotateMode: WhiteboardAnnotateMode.Paint,
  },
  scrim: {
    show: false,
    label: '',
  },
};

@State<ViewStateModel>({
  name: 'view',
  defaults: initialViewState,
})
@Injectable()
export class ViewState implements NgxsOnInit {
  // ---------- Selectors ----------
  @Selector()
  static getUI(state: ViewStateModel) {
    return state.ui;
  }

  @Selector()
  static getInitialUI(state: ViewStateModel) {
    return state.initialUI;
  }

  @Selector()
  static getLabelsBreakpoint(state: ViewStateModel) {
    return state.labelsBreakpoint;
  }

  @Selector()
  static getInputEnabled(state: ViewStateModel) {
    return state.inputEnabled;
  }

  @Selector()
  static getFullscreenActive(state: ViewStateModel) {
    return state.fullscreenActive;
  }

  @Selector()
  static getPresentationActive(state: ViewStateModel) {
    return state.presentationActive;
  }

  @Selector()
  static getDrawerCoversContent(state: ViewStateModel) {
    return state.drawerCoversContent;
  }

  @Selector()
  static getShowNodeInfo(state: ViewStateModel) {
    return state.nodeInfo.show;
  }

  @Selector()
  static getNodeInfoFallback(state: ViewStateModel) {
    return state.nodeInfo.fallback;
  }

  @Selector()
  static getViewLanguage(state: ViewStateModel) {
    return state.lang;
  }

  @Selector()
  static getInteractType(state: ViewStateModel) {
    return state.interactType;
  }

  @Selector()
  static getOnDemand(state: ViewStateModel) {
    return state.onDemand;
  }

  @Selector()
  static getLayout(state: ViewStateModel) {
    return state.layout;
  }

  @Selector()
  static getInputMode(state: ViewStateModel) {
    return state.inputMode;
  }

  @Selector()
  static getShowHelpTab(state: ViewStateModel) {
    return state.help.tab;
  }

  @Selector()
  static getTutorial(state: ViewStateModel) {
    return state.tutorial;
  }

  @Selector()
  static getTutorialSeen(state: ViewStateModel) {
    return state.tutorialSeen;
  }

  @Selector()
  static getWhiteboardEnabled(state: ViewStateModel) {
    return state.whiteboard.enabled;
  }

  @Selector()
  static getWhiteboardBrushSize(state: ViewStateModel) {
    return state.whiteboard.brushSize;
  }

  @Selector()
  static getWhiteboardTextSize(state: ViewStateModel) {
    return state.whiteboard.textSize;
  }

  @Selector()
  static getWhiteboardEraserSize(state: ViewStateModel) {
    return state.whiteboard.eraserSize;
  }

  @Selector()
  static getWhiteboardAnnotateMode(state: ViewStateModel) {
    return state.whiteboard.annotateMode;
  }

  @Selector()
  static getWhiteboardBrushColor(state: ViewStateModel) {
    return state.whiteboard.brushColor;
  }

  @Selector()
  static getWhiteboardHasPaint(state: ViewStateModel) {
    return state.whiteboard.hasPaint;
  }

  @Selector()
  static getWhiteboardHasText(state: ViewStateModel) {
    return state.whiteboard.hasText;
  }

  @Selector()
  static getCanTouch(state: ViewStateModel) {
    return state.canTouch;
  }

  @Selector()
  static getCanHover(state: ViewStateModel) {
    return state.canHover;
  }

  @Selector()
  static getMultiPickModeEnabled(state: ViewStateModel) {
    return state.multiPickModeEnabled;
  }

  @Selector()
  static getSearchDisplayMode(state: ViewStateModel) {
    return state.searchDisplayMode;
  }

  @Selector()
  static getUsePrintImage(state: ViewStateModel) {
    return state.usePrintImage;
  }

  @Selector()
  static getOpenWindow(state: ViewStateModel) {
    return state.openWindows.length > 0 ? state.openWindows.sort()[0] : null;
  }

  @Selector()
  static getMenuOpen(state: ViewStateModel) {
    return state.menuOpen;
  }

  @Selector()
  static getScrim(state: ViewStateModel) {
    return state.scrim;
  }

  static shouldWindowOpen(window: ViewWindow) {
    return createSelector([ViewState], (state: ViewStateModel) => {
      return state.openWindows.indexOf(window) >= 0;
    });
  }

  // ---------- Actions ----------
  @Action(ViewActions.SetUI)
  setUI(context: StateContext<ViewStateModel>, action: ViewActions.SetUI) {
    const ui = context.getState().ui;

    context.patchState({
      ui: {
        ...ui,
        ...action.ui,
      },
    });
  }

  @Action(ViewActions.SetInitialUI)
  setInitialUI(context: StateContext<ViewStateModel>, action: ViewActions.SetInitialUI) {
    context.patchState({
      initialUI: action.ui,
    });
  }

  @Action(ViewActions.SetInputEnabled)
  setInputEnabled(context: StateContext<ViewStateModel>, action: ViewActions.SetInputEnabled) {
    context.patchState({
      inputEnabled: action.enabled,
    });
  }

  @Action(ViewActions.SetCanHover)
  setCanHover(context: StateContext<ViewStateModel>, action: ViewActions.SetCanHover) {
    context.patchState({
      canHover: action.canHover,
    });
  }

  @Action(ViewActions.SetCanTouch)
  setCanTouch(context: StateContext<ViewStateModel>, action: ViewActions.SetCanTouch) {
    context.patchState({
      canTouch: action.canTouch,
    });
  }

  @Action(ViewActions.SetCanFullscreen)
  setCanFullscreen(context: StateContext<ViewStateModel>, action: ViewActions.SetCanFullscreen) {
    context.patchState({
      canFullscreen: action.canFullscreen,
    });
  }

  @Action(ViewActions.SetSearchDisplayMode)
  setSearchDisplayMode(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetSearchDisplayMode,
  ) {
    context.patchState({
      searchDisplayMode: action.mode,
    });
  }

  @Action(ViewActions.SetViewLanguage)
  setViewLanguage(context: StateContext<ViewStateModel>, { lang }: ViewActions.SetViewLanguage) {
    context.patchState({
      lang,
    });
  }

  @Action(ViewActions.ToggleNodeInfo)
  toggleNodeInfo(context: StateContext<ViewStateModel>, { show }: ViewActions.ToggleNodeInfo) {
    context.patchState({
      nodeInfo: {
        ...context.getState().nodeInfo,
        show,
      },
    });
  }

  @Action(ViewActions.SetNodeInfoConfig)
  setNodeInfoConfig(
    context: StateContext<ViewStateModel>,
    { fallback }: ViewActions.SetNodeInfoConfig,
  ) {
    context.patchState({
      nodeInfo: {
        ...context.getState().nodeInfo,
        fallback,
      },
    });
  }

  @Action(ViewActions.TogglePresentation)
  togglePresentation(
    context: StateContext<ViewStateModel>,
    action: ViewActions.TogglePresentation,
  ) {
    context.patchState({
      presentationActive: action.active,
    });
  }

  @Action(ViewActions.ToggleHelpTab)
  toggleHelp(context: StateContext<ViewStateModel>, action: ViewActions.ToggleHelpTab) {
    context.patchState({
      help: {
        tab: action.tab,
      },
    });
  }

  @Action(ViewActions.SetInteractType)
  setLoaderType(context: StateContext<ViewStateModel>, action: ViewActions.SetInteractType) {
    context.patchState({
      interactType: action.interactType,
    });
  }

  @Action(ViewActions.SetOnDemand)
  setOnDemand(context: StateContext<ViewStateModel>, action: ViewActions.SetOnDemand) {
    context.patchState({
      onDemand: action.onDemand,
    });
  }

  @Action(ViewActions.SetInputMode)
  setInputMode(context: StateContext<ViewStateModel>, { inputMode }: ViewActions.SetInputMode) {
    context.patchState({
      inputMode,
    });
  }

  @Action(ViewActions.SetLayout)
  SetLayout(context: StateContext<ViewStateModel>, action: ViewActions.SetLayout) {
    context.patchState({
      layout: action.layout,
    });
  }

  @Action(ViewActions.SetTutorial)
  setTutorial(context: StateContext<ViewStateModel>, action: ViewActions.SetTutorial) {
    const tutorial = context.getState().tutorial;
    context.patchState({
      tutorial: {
        ...tutorial,
        ...action.tutorial,
      },
    });
  }

  @Action(ViewActions.StartTutorial)
  startTutorial(context: StateContext<ViewStateModel>, action: ViewActions.StartTutorial) {
    context.patchState({
      tutorial: {
        ...context.getState().tutorial,
        pageIndex: action.intro ? -1 : 0,
      },
    });

    context.dispatch(new ViewActions.TutorialSeen(true));
  }

  @Action(ViewActions.TutorialSeen)
  tutorialSeen(context: StateContext<ViewStateModel>, action: ViewActions.TutorialSeen) {
    this.storageService.setLocalItem(LocalStorageEnum.GettingStartedTutorial, action.seen);

    context.patchState({
      tutorialSeen: action.seen,
    });
  }

  @Action(ViewActions.TutorialPrevious)
  tutorialPrevious(context: StateContext<ViewStateModel>, action: ViewActions.TutorialPrevious) {
    const tutorial = context.getState().tutorial;
    // normalize index
    const pageIndex = Math.max(tutorial.pageIndex - 1, 0);

    context.dispatch(
      new ViewActions.SetTutorial({
        ...tutorial,
        pageIndex: pageIndex,
      }),
    );
  }

  @Action(ViewActions.TutorialNext)
  tutorialNext(context: StateContext<ViewStateModel>, action: ViewActions.TutorialNext) {
    const tutorial = context.getState().tutorial;
    // normalize index
    const pageIndex = Math.min(tutorial.pageIndex + 1, tutorial.pageSize - 1);

    context.dispatch(
      new ViewActions.SetTutorial({
        ...tutorial,
        pageIndex: pageIndex,
      }),
    );
  }

  @Action(ViewActions.SetWhiteboardEnabled)
  setWhiteboardEnabled(
    context: StateContext<ViewStateModel>,
    { enabled }: ViewActions.SetWhiteboardEnabled,
  ) {
    context.patchState({
      whiteboard: {
        ...context.getState().whiteboard,
        enabled,
      },
    });
  }

  @Action(ViewActions.SetWhiteboardBrushSize)
  setWhiteboardBrushSize(
    context: StateContext<ViewStateModel>,
    { brushSize }: ViewActions.SetWhiteboardBrushSize,
  ) {
    context.patchState({
      whiteboard: {
        ...context.getState().whiteboard,
        brushSize,
      },
    });
  }

  @Action(ViewActions.SetWhiteboardTextSize)
  setWhiteboardTextSize(
    context: StateContext<ViewStateModel>,
    { size }: ViewActions.SetWhiteboardTextSize,
  ) {
    context.patchState({
      whiteboard: {
        ...context.getState().whiteboard,
        textSize: size,
      },
    });
  }

  @Action(ViewActions.SetWhiteboardEraserSize)
  setWhiteboardEraserSize(
    context: StateContext<ViewStateModel>,
    { eraserSize }: ViewActions.SetWhiteboardEraserSize,
  ) {
    context.patchState({
      whiteboard: {
        ...context.getState().whiteboard,
        eraserSize,
      },
    });
  }

  @Action(ViewActions.SetWhiteboardAnnotateMode)
  setWhiteboardAnnotateMode(
    context: StateContext<ViewStateModel>,
    { mode }: ViewActions.SetWhiteboardAnnotateMode,
  ) {
    context.patchState({
      whiteboard: {
        ...context.getState().whiteboard,
        annotateMode: mode,
      },
    });
  }

  @Action(ViewActions.SetWhiteboardBrushColor)
  setWhiteboardBrushColor(
    context: StateContext<ViewStateModel>,
    { brushColor }: ViewActions.SetWhiteboardBrushColor,
  ) {
    context.patchState({
      whiteboard: {
        ...context.getState().whiteboard,
        brushColor,
      },
    });
  }

  @Action(ViewActions.SetWhiteboardHasPaint)
  setWhiteboardHasPaint(
    context: StateContext<ViewStateModel>,
    { hasPaint }: ViewActions.SetWhiteboardHasPaint,
  ) {
    context.patchState({
      whiteboard: {
        ...context.getState().whiteboard,
        hasPaint,
      },
    });
  }

  @Action(ViewActions.SetWhiteboardHasText)
  setWhiteboardHasText(
    context: StateContext<ViewStateModel>,
    { hasText }: ViewActions.SetWhiteboardHasText,
  ) {
    context.patchState({
      whiteboard: {
        ...context.getState().whiteboard,
        hasText,
      },
    });
  }

  @Action(ViewActions.SetMultiPickModeEnabled)
  setMultiPickModeEnabled(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetMultiPickModeEnabled,
  ) {
    context.patchState({
      multiPickModeEnabled: action.enabled,
    });
  }

  @Action(ViewActions.SetLabelBreakpoint)
  setLabelBreakpoint(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetLabelBreakpoint,
  ) {
    context.patchState({
      labelsBreakpoint: action.labelsBreakpoint,
    });
  }

  @Action(ViewActions.SetUsePrintImage)
  setUsePrintImage(context: StateContext<ViewStateModel>, action: ViewActions.SetUsePrintImage) {
    context.patchState({
      usePrintImage: action.usePrintImage,
    });
  }

  @Action(ViewActions.SetWindows)
  setWindows(context: StateContext<ViewStateModel>, action: ViewActions.SetWindows) {
    context.patchState({
      openWindows: action.windows,
    });
  }

  @Action(ViewActions.OpenWindow)
  openWindow(context: StateContext<ViewStateModel>, action: ViewActions.OpenWindow) {
    const openWindows = context.getState().openWindows;

    if (openWindows.indexOf(action.window) < 0) {
      context.patchState({
        openWindows: [action.window, ...openWindows],
      });
    }
  }

  @Action(ViewActions.CloseWindow)
  closeWindow(context: StateContext<ViewStateModel>, action: ViewActions.CloseWindow) {
    let openWindows = context.getState().openWindows;

    if (action.window != null) {
      openWindows = openWindows.filter((window) => window !== action.window);
    } else {
      openWindows.shift();
    }

    context.patchState({
      openWindows: [...openWindows],
    });
  }

  @Action(ViewActions.ToggleWindow)
  toggleWindow(context: StateContext<ViewStateModel>, action: ViewActions.ToggleWindow) {
    const isOpen = context.getState().openWindows.indexOf(action.window) >= 0;
    const show = action.show != null ? action.show : !isOpen;

    show
      ? context.dispatch(new ViewActions.OpenWindow(action.window))
      : context.dispatch(new ViewActions.CloseWindow(action.window));
  }

  @Action(ViewActions.ToggleMenu)
  toggleMenu(context: StateContext<ViewStateModel>, action: ViewActions.ToggleMenu) {
    context.patchState({
      menuOpen: action.open,
    });
  }

  @Action(ViewActions.ShowScrim)
  showScrim(context: StateContext<ViewStateModel>, action: ViewActions.ShowScrim) {
    context.patchState({
      scrim: {
        show: true,
        label: action.label,
      },
    });
  }

  @Action(ViewActions.HideScrim)
  hideScrim(context: StateContext<ViewStateModel>, action: ViewActions.HideScrim) {
    context.patchState({
      scrim: {
        show: false,
        label: '',
      },
    });
  }

  constructor(private storageService: StorageService) {}

  ngxsOnInit(context: StateContext<ViewStateModel>) {
    const fullscreen$ = Fullscreen.onFullscreenChange();

    fullscreen$.subscribe((event) => {
      context.patchState({
        fullscreenActive: Fullscreen.isFullscreen(),
      });
    });

    context.patchState({
      tutorialSeen:
        this.storageService.getLocalJSON(LocalStorageEnum.GettingStartedTutorial) || false,
    });
  }
}
